import { useState, memo, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { userDashboardRoute } from "../../../../utils/routes/Routes";
import RowContainer from "../../../common/RowContainer";
import { TableWrapper, StyledTable, TableHeader } from "../../style";
import "./TicketsStyle.less";
import { FilterAlt, FilterAltOff, Info } from "@mui/icons-material";
import { TableCell, Typography } from "@mui/material";
import { changePageNumberTickets } from "../../../../redux/features/tickets/ticketsSlice";
import MultipleFilterComponent from "../../../common/MultipleFilterComponent";
import { RenderColoredCircleIconWithStatus } from "../../../common/RenderColoredCircleIconWithStatus";
import { FormattedMessage } from "react-intl";
import { FormatDateTime } from "../../../../utils/helpers/FormatDateTime";
import { DashboardButton } from "../../../common/DashboardButton";
import { FilterButton } from "../../../common/FilterButton";
import ColumnContainer from "../../../common/ColumnContainer";
import { translateFilterKeys } from "../../../../utils/helpers/translateFilterKeys";
import { selectLanguage } from "../../../../redux/features/language/languageSlice";

export const UserTicketsList = memo(function ({
  tickets,
  paginationData,
  notFilteredTickets,
  onFilterStateChange,
  onTicketsPageChange,
}) {
  // INIT
  const dispatch = useDispatch();
  const currentLanguage = useSelector(selectLanguage);

  // STATE
  const [filters, setFilters] = useState({
    ticket_status: null,
    category: null,
    company_name: null,
    created_at: null,
    updated_at: null,
  });
  const [isFilteringActive, setIsFilteringActive] = useState(false);

  // FUNCTIONS
  const resetFiltersAndPage = useCallback(() => {
    setFilters({
      ticket_status: null,
      category: null,
      company_name: null,
      created_at: null,
      updated_at: null,
    });
    onFilterStateChange({
      ticket_status: null,
      category: null,
      company_name: null,
      created_at: null,
      updated_at: null,
    });
  }, [dispatch, onFilterStateChange]);

  const handleFilterChange = (key, value) => {
    // lift the state up to the parent (UserPage)
    const newFilterValue = value;
    setFilters((prevState) => {
      return { ...prevState, [key]: newFilterValue };
    });
    onFilterStateChange((filters) => ({
      ...filters,
      [key]: newFilterValue,
    }));
    dispatch(changePageNumberTickets(1));
  };

  const handleToggleFilters = () => {
    setIsFilteringActive((prevState) => {
      const newState = !prevState;
      if (!newState) {
        resetFiltersAndPage();
      }
      return newState;
    });
  };

  const renderTicketsRows = () => {
    return tickets?.paginated_results?.map((row, index) => (
      <tr key={index}>
        <TableCell>{row.id}</TableCell>
        <TableCell>
          <RenderColoredCircleIconWithStatus string={row?.status} />
        </TableCell>
        <TableCell>
          {translateFilterKeys(row?.category?.name, currentLanguage)}
        </TableCell>
        <TableCell>{row?.company_name}</TableCell>
        <TableCell>
          {<FormatDateTime value={row?.created_at} /> || "-"}
        </TableCell>
        <TableCell>
          {<FormatDateTime value={row?.updated_at} /> || "-"}
        </TableCell>
        <TableCell>{row?.title}</TableCell>
        <TableCell>
          <Link to={`${userDashboardRoute.tickets}/${row.id}`}>
            <DashboardButton type="filled">
              {" "}
              <FormattedMessage id="button.details" />
            </DashboardButton>
          </Link>
        </TableCell>
      </tr>
    ));
  };
  // EFFECTS
  useEffect(() => {
    if (!isFilteringActive) {
      resetFiltersAndPage();
      onTicketsPageChange(1);
    }
  }, [isFilteringActive]);

  // UTILS
  const tableHeaders = [
    "Id",
    <FormattedMessage id="table.header.status" />,
    <FormattedMessage id="table.header.category" />,
    <FormattedMessage id="table.header.companyName" />,
    <FormattedMessage id="table.header.created" />,
    <FormattedMessage id="table.header.updated" />,
    <FormattedMessage id="table.header.title" />,
    <FormattedMessage id="table.header.details" />,
  ];

  return (
    <TableWrapper className="projects-table">
      <ColumnContainer className="project-filter">
        <RowContainer align="center" minGap>
          <FilterButton
            isActive={isFilteringActive}
            onClick={handleToggleFilters}
            disabled={
              tickets?.paginated_results?.length === 0 ||
              tickets?.paginated_results === null
            }
          >
            <RowContainer center minGap>
              {isFilteringActive ? <FilterAlt /> : <FilterAltOff />}
              <Typography
                variant="p"
                color="var(--text-primary) !important"
                fontWeight="400 !important"
                fontSize={"16px !important"}
              >
                <FormattedMessage id="button.filter" />
              </Typography>
            </RowContainer>
          </FilterButton>
          <RowContainer align="center" minGap>
            <Info color="warning" />
            <Typography variant="p" color="var(--text-primary)">
              <Link to={userDashboardRoute.projects}>
                <FormattedMessage id="ticket.help" />
              </Link>
            </Typography>
          </RowContainer>
        </RowContainer>
        <RowContainer align="start" style={{ paddingTop: "20px" }}>
          {isFilteringActive ? (
            <MultipleFilterComponent
              filters={filters}
              filterOptions={tickets.filter_options}
              handleFilterChange={handleFilterChange}
              tableData={notFilteredTickets || []}
              paginationData={paginationData}
            />
          ) : null}
        </RowContainer>
      </ColumnContainer>
      {tickets?.paginated_results?.length > 0 ? (
        <StyledTable>
          <thead>
            <tr>
              {tableHeaders.map((header, index) => (
                <TableHeader key={index}>{header}</TableHeader>
              ))}
            </tr>
          </thead>
          {tickets?.paginated_results?.length > 0 && renderTicketsRows()}
        </StyledTable>
      ) : (
        <Typography
          variant="h6"
          component="h6"
          fontWeight={"400"}
          color="var(--text-secondary)"
        >
          <FormattedMessage id="table.noData" />
        </Typography>
      )}
    </TableWrapper>
  );
});

export default UserTicketsList;
